import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TextFieldLabel from '@shared/components/inputs/TextFieldLabel/TextFieldLabel';
import SelectField from '@shared/components/inputs/SelectField/SelectField';
import FormErrorMessage from '@shared/components/feedback/FormErrorMessage/FormErrorMessage';
import { StyledPhoneNumberInput } from '@shared/components/inputs/PhoneNumberInput/PhoneNumberInput.styles';
import { noAutofillSx } from '@components/Checkout/steps/CustomerInfoStep/utils/customerInfoStepUtils';
import type { ContactInfoSectionProps } from '@components/Checkout/steps/CustomerInfoStep/CustomerInfoStep.types';

export const ContactInfoSection = ({ formik, phoneTypeOptions }: ContactInfoSectionProps) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = formik;

  const selectedPhoneType = phoneTypeOptions.find((o) => o.value === values.primaryPhoneNumberType) ?? null;
  const showPhoneError = touched.primaryPhoneNumber && !!errors.primaryPhoneNumber;

  const handlePhoneTypeChange = (option: unknown) => {
    const opt = option as { value?: string } | null;
    setFieldValue('primaryPhoneNumberType', opt?.value ?? '');
  };

  return (
    <>
      <Typography variant="heading.subsection" sx={{ lineHeight: '150%' }}>
        Contact Information
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} gap="15px" alignItems="flex-end">
        <Stack sx={{ width: { xs: '100%', sm: '50%' } }} gap="10px">
          <TextFieldLabel label="Primary Contact Number" />
          <StyledPhoneNumberInput
            placeholder="(123) 456-7890"
            value={values.primaryPhoneNumber}
            onChange={(value) => setFieldValue('primaryPhoneNumber', value || '')}
            onBlur={() => setFieldTouched('primaryPhoneNumber', true)}
            borderRadius="6px"
            height="41px"
            invalid={showPhoneError}
            defaultCountry="CA"
            fontSize="16px"
          />
          {showPhoneError && <FormErrorMessage>{errors.primaryPhoneNumber as string}</FormErrorMessage>}
        </Stack>
        <Stack sx={{ width: { xs: '100%', sm: '50%' } }} gap="10px">
          <SelectField
            placeholder="Select Option"
            options={phoneTypeOptions}
            value={selectedPhoneType}
            onChange={handlePhoneTypeChange}
            onBlur={() => setFieldTouched('primaryPhoneNumberType', true)}
            error={touched.primaryPhoneNumberType && !!errors.primaryPhoneNumberType}
            helperText={touched.primaryPhoneNumberType && (errors.primaryPhoneNumberType as string)}
          />
        </Stack>
      </Stack>

      <Stack gap="10px">
        <TextFieldLabel label="Email" />
        <TextField
          sx={noAutofillSx}
          name="email"
          type="email"
          placeholder="Enter your email..."
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.email && !!errors.email}
          helperText={
            touched.email && errors.email ? <FormErrorMessage>{errors.email as string}</FormErrorMessage> : null
          }
        />
      </Stack>

      <Stack gap="10px">
        <TextFieldLabel label="Confirm Email Address" />
        <TextField
          sx={noAutofillSx}
          name="confirmEmail"
          type="email"
          placeholder="Confirm your email..."
          value={values.confirmEmail}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.confirmEmail && !!errors.confirmEmail}
          helperText={
            touched.confirmEmail && errors.confirmEmail ? (
              <FormErrorMessage>{errors.confirmEmail as string}</FormErrorMessage>
            ) : null
          }
        />
      </Stack>
    </>
  );
};
